import { useCallback, useRef, useEffect } from 'react';
import { logToDatadog } from '../../../services/datadog';

interface PerformanceMetrics {
  renderTime: number;
  apiCallTime: number;
  cacheHitRate: number;
  memoryUsage?: number;
}

export const usePerformanceMonitoring = () => {
  const renderStartTime = useRef<number>(0);
  const apiMetrics = useRef<{ total: number; cached: number }>({ total: 0, cached: 0 });
  const performanceObserver = useRef<PerformanceObserver | null>(null);

  // Setup Performance Observer for Web Vitals (mounted-only)
  useEffect(() => {
    if (typeof PerformanceObserver !== 'undefined') {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'largest-contentful-paint') {
            logToDatadog('web_vital_lcp', {
              value: entry.startTime,
              timestamp: Date.now(),
            });
          }
          if (entry.entryType === 'first-input') {
            logToDatadog('web_vital_fid', {
              value: (entry as any).processingStart - entry.startTime,
              timestamp: Date.now(),
            });
          }
          if (entry.entryType === 'layout-shift') {
            logToDatadog('web_vital_cls', {
              value: (entry as any).value,
              timestamp: Date.now(),
            });
          }
        }
      });
      try {
        observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'] });
        performanceObserver.current = observer;
      } catch (error) {
        console.warn('Performance Observer not supported:', error);
      }
    }
    return () => {
      if (performanceObserver.current) {
        performanceObserver.current.disconnect();
      }
    };
  }, []);


  // Get current cache hit rate
  const getCacheHitRate = useCallback((): number => {
    const { total, cached } = apiMetrics.current;
    return total > 0 ? (cached / total) * 100 : 0;
  }, []);

  // Get memory usage (if available)
  const getMemoryUsage = useCallback((): { used: number; total: number; limit: number } | undefined => {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return {
        used: memory.usedJSHeapSize,
        total: memory.totalJSHeapSize,
        limit: memory.jsHeapSizeLimit,
      };
    }
    return undefined;
  }, []);

  // Log performance summary
  const logPerformanceSummary = useCallback(() => {
    const metrics: PerformanceMetrics = {
      renderTime: 0, // This would be average over time
      apiCallTime: 0, // This would be average over time
      cacheHitRate: getCacheHitRate(),
      memoryUsage: getMemoryUsage()?.used ?? 0, //TODO-Alan: is this right ? 
    };
    
    logToDatadog('performance_summary', {
      metrics,
      timestamp: Date.now(),
    });
    
    return metrics;
  }, [getCacheHitRate, getMemoryUsage]);

  // Log performance summary periodically (mounted-only)
  useEffect(() => {
    const interval = setInterval(() => {
      logPerformanceSummary();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [logPerformanceSummary]);
  
  // Start measuring render time
  const startRenderMeasurement = useCallback(() => {
    renderStartTime.current = performance.now();
  }, []);

  // End measuring render time and log if significant
  const endRenderMeasurement = useCallback((componentName: string) => {
    if (renderStartTime.current > 0) {
      const renderTime = performance.now() - renderStartTime.current;
      
      // Log slow renders (> 16ms for 60fps)
      if (renderTime > 16) {
        logToDatadog('slow_render_detected', {
          component: componentName,
          renderTime,
          timestamp: Date.now(),
        });
      }
      
      renderStartTime.current = 0;
      return renderTime;
    }
    return 0;
  }, []);

  // Measure API call performance
  const measureApiCall = useCallback(async <T>(
    operation: () => Promise<T>,
    operationName: string,
    fromCache: boolean = false
  ): Promise<T> => {
    const startTime = performance.now();
    
    try {
      const result = await operation();
      const duration = performance.now() - startTime;
      
      // Update metrics
      apiMetrics.current.total++;
      if (fromCache) {
        apiMetrics.current.cached++;
      }
      
      // Log API performance
      logToDatadog('api_call_performance', {
        operation: operationName,
        duration,
        fromCache,
        timestamp: Date.now(),
      });
      
      // Log slow API calls (> 1000ms)
      if (duration > 1000) {
        logToDatadog('slow_api_call_detected', {
          operation: operationName,
          duration,
          fromCache,
          timestamp: Date.now(),
        });
      }
      
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      
      logToDatadog('api_call_error', {
        operation: operationName,
        duration,
        error,
        timestamp: Date.now(),
      });
      
      throw error;
    }
  }, []);

  // Reset metrics
  const resetMetrics = useCallback(() => {
    apiMetrics.current = { total: 0, cached: 0 };
  }, []);

  // Mark significant user interactions
  const markUserInteraction = useCallback((interaction: string, metadata?: any) => {
    logToDatadog('user_interaction', {
      interaction,
      metadata,
      timestamp: Date.now(),
    });
  }, []);

  // Custom performance marks
  const mark = useCallback((name: string) => {
    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark(name);
    }
  }, []);

  const measure = useCallback((name: string, startMark: string, endMark?: string) => {
    if (typeof performance !== 'undefined' && performance.measure) {
      try {
        performance.measure(name, startMark, endMark);
        
        // Get the measurement
        const measures = performance.getEntriesByName(name, 'measure');
        if (measures.length > 0) {
          const duration = measures[measures.length - 1].duration;
          
          logToDatadog('custom_performance_measure', {
            name,
            duration,
            timestamp: Date.now(),
          });
          
          return duration;
        }
      } catch (error) {
        console.warn('Performance measurement failed:', error);
      }
    }
    return 0;
  }, []);

  return {
    // Render performance
    startRenderMeasurement,
    endRenderMeasurement,
    
    // API performance
    measureApiCall,
    getCacheHitRate,
    
    // Memory and system
    getMemoryUsage,
    
    // Reporting
    logPerformanceSummary,
    resetMetrics,
    
    // User interactions
    markUserInteraction,
    
    // Custom measurements
    mark,
    measure,
  };
};
